import React, { useState, useEffect } from 'react';
import { Image, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ApiFetch from './ApiFetch';
import * as Icon from 'react-bootstrap-icons';

const UserAvatarWithLikes = ({ 
    userId, 
    userDisplayId, 
    userName, 
    hash, 
    size = 50, 
    showLikes = true, 
    linkToProfile = true,
    linkTo, // カスタムリンク先
    borderRadius = "50%", 
    style = {},
    badgeStyle = {},
    className,
    ...props 
}) => {
    const [likeCount, setLikeCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // APIからユーザーのいいね数を取得
    useEffect(() => {
        if (showLikes && userId && userId !== "anonymous") {
            setLoading(true);
            fetchUserLikes();
        }
    }, [userId, showLikes]);

    const fetchUserLikes = async () => {
        try {
            const response = await new ApiFetch().get(
                `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/profile/${userId}`
            );
            const data = await response.json();
            if (data.result) {
                setLikeCount(data.favorite || 0); // いいねされた数を取得
            }
        } catch (error) {
            console.error('Failed to fetch user likes:', error);
        } finally {
            setLoading(false);
        }
    };

    // 数値をK、M表記にフォーマットする関数
    const formatLikeCount = (count) => {
        if (count >= 1000000) {
            return (count / 1000000).toFixed(1) + 'M';
        } else if (count >= 1000) {
            return (count / 1000).toFixed(1) + 'K';
        }
        return count.toString();
    };

    const getImageSrc = () => {
        if (!userId || userId === "anonymous") {
            return null;
        }
        const baseUrl = `${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/profile/${userId}`;
        const sizeParam = size <= 100 ? '_s' : size <= 300 ? '_m' : '';
        const hashParam = hash ? `?${hash}` : '';
        return `${baseUrl}${sizeParam}.jpg${hashParam}`;
    };

    const renderAvatar = () => {
        const imageSrc = getImageSrc();
        
        if (!imageSrc) {
            return (
                <div 
                    style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        borderRadius,
                        backgroundColor: '#CCCCCC',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...style
                    }}
                >
                    <Icon.PersonFill style={{ fontSize: `${size * 0.6}px`, color: '#FFFFFF' }} />
                </div>
            );
        }

        return (
            <Image 
                src={imageSrc}
                className={className}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius,
                    objectFit: 'cover',
                    ...style
                }}
                {...props}
            />
        );
    };

    const avatarContent = (
        <div style={{ 
            position: 'relative', 
            display: 'inline-block',
            width: `${size}px`,
            height: `${size}px`
        }}>
            {renderAvatar()}
            
            {showLikes && likeCount > 0 && (
                <Badge 
                    bg="danger" 
                    style={{
                        position: 'absolute',
                        bottom: `${size * 0.05}px`,
                        right: `${size * 0.05}px`,
                        fontSize: `${Math.max(8, size * 0.1)}px`,
                        minWidth: `${Math.max(16, size * 0.18)}px`,
                        height: `${Math.max(16, size * 0.18)}px`,
                        borderRadius: `${Math.max(8, size * 0.09)}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `${Math.max(1, size * 0.02)}px solid #FFFFFF`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        ...badgeStyle
                    }}
                >
                    <Icon.HeartFill style={{ fontSize: `${Math.max(6, size * 0.08)}px`, marginRight: '1px' }} />
                    {formatLikeCount(likeCount)}
                </Badge>
            )}

            {loading && showLikes && (
                <div 
                    style={{
                        position: 'absolute',
                        bottom: `${size * 0.05}px`,
                        right: `${size * 0.05}px`,
                        width: `${Math.max(16, size * 0.18)}px`,
                        height: `${Math.max(16, size * 0.18)}px`,
                        borderRadius: `${Math.max(8, size * 0.09)}px`,
                        backgroundColor: '#FFFFFF',
                        border: `${Math.max(1, size * 0.02)}px solid #CCCCCC`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                >
                    <div 
                        style={{
                            width: `${Math.max(6, size * 0.08)}px`,
                            height: `${Math.max(6, size * 0.08)}px`,
                            border: `1px solid #CCCCCC`,
                            borderTop: `1px solid #007bff`,
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}
                    />
                </div>
            )}
        </div>
    );

    if ((linkToProfile || linkTo) && userId && userId !== "anonymous") {
        const targetLink = linkTo || `/user/${userDisplayId && userDisplayId !== "" ? userDisplayId : userId}`;
        return (
            <Link 
                to={targetLink} 
                style={{ textDecoration: 'none' }}
                title={userName}
            >
                {avatarContent}
            </Link>
        );
    }

    return avatarContent;
};

// 回転アニメーションのCSS
const spinKeyframes = `
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
`;

// CSSをheadに追加
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = spinKeyframes;
    document.head.appendChild(style);
}

export default UserAvatarWithLikes;
